/**
 * Turning a storage failure into something the caller can act on.
 *
 * The store never lets a rejection escape. A caller that has just applied an edit in memory has
 * exactly one decision to make — "may I tell the user this is saved?" — and it is the same
 * decision whether the disk was full, the database was closed by the browser, or a transaction
 * aborted. A `Result` puts that decision in the type system instead of in a `try` somebody has
 * to remember to write.
 */
import { appError, isRetryable } from '@orbit-mind/shared-types';
/**
 * What an adapter throws.
 *
 * Both a real `Error` — so it carries a stack, and so `throw` means what it means everywhere
 * else — and an `AppError`, so the code survives the trip. The distinction between
 * `QUOTA_EXCEEDED` and `STORAGE_UNAVAILABLE` is the distinction between "the user must free
 * space" and "try again shortly", and recovering it by matching on a message string is how that
 * decision quietly stops working after a browser update.
 */
export class StorageError extends Error {
    code;
    retryable;
    details;
    constructor(code, message, options) {
        super(message, options?.cause === undefined ? undefined : { cause: options.cause });
        this.name = 'StorageError';
        this.code = code;
        this.retryable = options?.retryable ?? isRetryable(code);
        if (options?.details !== undefined)
            this.details = options.details;
    }
}
/**
 * Does this thrown value already carry a code?
 *
 * Adapters are encouraged to throw `AppError`s — a Dexie adapter knows that `QuotaExceededError`
 * means `QUOTA_EXCEEDED`, and that distinction is the difference between "retry later" and "the
 * user must free space". Anything else is reported as an unavailable store.
 */
export function isAppError(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return (typeof candidate.code === 'string' &&
        typeof candidate.message === 'string' &&
        typeof candidate.retryable === 'boolean');
}
/** Wraps whatever an adapter threw, keeping a coded error's meaning intact. */
export function storageFailure(operation, cause) {
    if (isAppError(cause))
        return cause;
    return appError('STORAGE_UNAVAILABLE', `journal ${operation} failed`, {
        details: { operation },
        cause,
    });
}
/** For the cases the store rejects itself, before any adapter is involved. */
export function journalError(code, message, details) {
    return details === undefined ? appError(code, message) : appError(code, message, { details });
}
//# sourceMappingURL=errors.js.map